import React, { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useIntl } from 'react-intl';
import { useDebouncedCallback } from 'use-debounce';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap } from '@atlaskit/css';
import AddIcon from '@atlaskit/icon/core/add';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';
import SearchIcon from '@atlaskit/icon/core/search';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { AgentProfileCardTrigger } from '@atlaskit/profilecard/agent-profile-card-trigger';
import { ProfileClient } from '@atlaskit/profilecard/profile-card-client';
import Spinner from '@atlaskit/spinner/spinner';
import Textfield from '@atlaskit/textfield/text-field';
import { token } from '@atlaskit/tokens';

import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	usePaginatedAgentsByCategory,
	useRecommendedAgents,
	usePaginatedAgentsActions,
} from '@atlassian/conversation-assistant-agent/controllers/agents';
import { useAgentFavourites } from '@atlassian/conversation-assistant-agent/controllers/agent-favourite';
import { getAtlassianStudioNLCreateUrl } from '@atlassian/conversation-assistant-agent/common/utils/atlassian-studio-url';
import { getAgentIcon } from '@atlassian/conversation-assistant-agent/utils/getDisplayedAgents';
import { MenuItem } from '@atlassian/conversation-assistant-ui-components/ui/chat-header-menu/MenuItem';
import { ChatHeaderMenuList } from '@atlassian/conversation-assistant-ui-components/ui/chat-header-menu/ChatHeaderMenuList';

import { messages } from './messages.ts';

// Wrapper to style the Textfield to match AgentMenu — using ref-based DOM manipulation
// to avoid @compiled/react styled limitations with child selectors
const TextfieldInputWrapper = ({ children }: { children: React.ReactNode }) => (
	<div
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
		style={{ display: 'flex', flexDirection: 'column', width: '100%' }}
	>
		{children}
	</div>
);

// Debounce delay (ms) for server-side agent search as the user types.
const SEARCH_DEBOUNCE_MS = 400;

// Known Atlassian Studio origins allowed for the "Create agent" deep link.
// Validated in handleCreateAgent to prevent potential open redirect or XSS vectors.
const ALLOWED_STUDIO_ORIGINS = [
	'https://studio.atlassian.com',
	'https://atlassian-studio.stg-east.frontend.public.atl-paas.net',
];

const styles = cssMap({
	heading: {
		paddingTop: token('space.150'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		backgroundColor: token('elevation.surface.overlay'),
	},
	loadingWrapper: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
	},
	errorWrapper: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
	},
	searchWrapper: {
		display: 'flex',
		paddingTop: token('space.050'),
		paddingRight: token('space.075'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.075'),
		flexDirection: 'column',
		alignSelf: 'stretch',
	},
	emptySection: {
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		paddingTop: token('space.050'),
		paddingBottom: token('space.050'),
	},
	// AgentProfileCardTrigger's internal trigger wrapper renders a plain, unstyled <span>
	// with no explicit width. Since the menu list container centers items (`alignItems:
	// 'center'`) rather than stretching them, that <span> shrinks to fit its content instead
	// of filling the row — making wrapped agents appear narrower/indented compared to
	// unwrapped ones. Forcing this outer wrapper to take up the full row width fixes it.
	profileCardTriggerWrapper: {
		display: 'block',
		width: '100%',
	},
});

interface AgentSearchInputProps {
	value: string;
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	placeholder: string;
}

const AgentSearchInput = React.forwardRef<HTMLInputElement, AgentSearchInputProps>(
	({ value, onChange, placeholder }, ref) => (
		<Box xcss={styles.searchWrapper}>
			<TextfieldInputWrapper>
				<Textfield
					ref={ref}
					value={value}
					onChange={onChange}
					placeholder={placeholder}
					testId="confluence-agent-menu-search-input"
					elemBeforeInput={
						<Box paddingInlineStart="space.100">
							<SearchIcon label="" />
						</Box>
					}
				/>
			</TextfieldInputWrapper>
		</Box>
	),
);
AgentSearchInput.displayName = 'AgentSearchInput';

interface AgentItemProps {
	agent: Agent;
	isSelected: boolean;
	onSelect: (agent: Agent) => void;
	onFavoriteToggle?: (agent: Agent) => void;
	cloudId: string;
	profileClient: ProfileClient;
}

const AgentItem = ({
	agent,
	isSelected,
	onSelect,
	onFavoriteToggle,
	cloudId,
	profileClient,
}: AgentItemProps) => {
	const handleClick = useCallback(() => {
		onSelect(agent);
	}, [agent, onSelect]);

	const handleFavoriteClick = useCallback(() => {
		onFavoriteToggle?.(agent);
	}, [agent, onFavoriteToggle]);

	const menuItem = (
		<MenuItem
			label={agent.name}
			icon={getAgentIcon(agent)}
			onClick={handleClick}
			isActive={isSelected}
			isFavorite={agent.favourite}
			isVerified={agent.is_verified}
			onFavoriteClick={onFavoriteToggle ? handleFavoriteClick : undefined}
			testId={`confluence-agent-menu-agent-item-${agent.id}`}
		/>
	);

	// Agents without a directory identity (e.g. some system/default agents) don't have
	// profile data to show — fall back to the plain item, matching AgentDropdownItem's
	// established pattern for the same profile card component elsewhere in the codebase.
	if (!agent.identity_account_id) {
		return menuItem;
	}

	return (
		<Box xcss={styles.profileCardTriggerWrapper}>
			<AgentProfileCardTrigger
				trigger="hover"
				agentId={agent.id}
				resourceClient={profileClient}
				cloudId={cloudId}
				hideStarButton
				onChatClick={handleClick}
				onConversationStartersClick={handleClick}
			>
				{menuItem}
			</AgentProfileCardTrigger>
		</Box>
	);
};

interface ConfluenceAgentMenuProps {
	cloudId: string;
	selectedAgentId?: string;
	onSelectAgent: (agent: Agent) => void;
	onBrowseAgents: () => void;
}

const MAX_FAVORITE_AGENTS = 5;

export const ConfluenceAgentMenu = ({
	cloudId,
	selectedAgentId,
	onSelectAgent,
	onBrowseAgents,
}: ConfluenceAgentMenuProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [searchInputValue, setSearchInputValue] = useState('');
	const [searchQuery, setSearchQuery] = useState('');
	const searchInputRef = useRef<HTMLInputElement>(null);

	// Focus search on mount
	useEffect(() => {
		searchInputRef.current?.focus();
	}, []);

	const assistanceServiceParams = useMemo(
		() => ({
			touchpointSource: 'confluence-object-sidebar-agents-button',
			product: 'confluence' as const,
			experienceId: 'ai-mate',
			siteId: cloudId,
			baseUrl: `/gateway/api/assist`,
		}),
		[cloudId],
	);

	// Client for the agent hover preview card (AgentProfileCardTrigger) — shows agent
	// details (description, verified badge, conversation starters) on hover, mirroring
	// the same pattern used by AgentDropdownItem elsewhere in the AI Mate codebase.
	const profileClient = useMemo(
		() =>
			new ProfileClient({
				url: '/gateway/api/directory/graphql',
				productIdentifier: 'rovo',
				cloudId,
			}),
		[cloudId],
	);

	// Fetch recommended agents
	const { recommendedAgents } = useRecommendedAgents({
		assistanceServiceParams,
	});

	// Defensive snapshot: `recommendedAgents` is expected to be a stable, one-time fetch for
	// the whole session (limit/pageUrl/siteId never change, and the underlying agent records
	// are never removed from the shared store) — but has been observed in practice to lose
	// entries after a favourite-then-unfavourite round trip, causing the Recommended section
	// to disappear and never recover (a full page reload does bring it back, which points to
	// something upstream in the shared platform store/cache rather than in this component).
	// Since the raw list is meant to be static anyway, we only ever grow this snapshot —
	// never let it shrink — so a transient upstream data loss can't hide the section from
	// under the user for the rest of the session. The seen-map is mutated during render (a
	// standard idempotent memoization pattern) rather than via an effect, since new ids are
	// only ever added and re-running this for the same input is a no-op.
	const seenRecommendedAgentsRef = useRef<Map<string, Agent>>(new Map());
	const stableRecommendedAgents = useMemo(() => {
		for (const agent of recommendedAgents) {
			if (!seenRecommendedAgentsRef.current.has(agent.id)) {
				seenRecommendedAgentsRef.current.set(agent.id, agent);
			}
		}
		return Array.from(seenRecommendedAgentsRef.current.values());
	}, [recommendedAgents]);

	// Fetch starred agents for favorites section
	const [{ agentsByCategory: starredAgents, isCategoryLoading: isStarredLoading }] =
		usePaginatedAgentsByCategory('starred-agents');

	// Fetch all agents for search
	const [{ agentsByCategory: allAgentsList, isCategoryLoading: isAllAgentsLoading, agentsError }] =
		usePaginatedAgentsByCategory('all-agents');

	const { fetchAgentsByCategory, populateAgentCategories } = usePaginatedAgentsActions({
		assistanceServiceParams,
	});

	const { favouriteAgent, unfavouriteAgent } = useAgentFavourites({
		assistanceServiceParams,
	});

	// Local optimistic overrides for favourite state, keyed by agent id. The server-fetched
	// agent lists (starred/recommended/all) don't reflect a toggle until the categories are
	// refetched, so we track pending overrides — including a snapshot of the agent itself —
	// to flip the star instantly and move the agent in/out of the Favorites section without
	// waiting on (or triggering) a network refetch.
	const [favoriteOverrides, setFavoriteOverrides] = useState<
		Record<string, { favourite: boolean; agent: Agent }>
	>({});

	// Deliberately do NOT trust the `favourite` field on agent objects coming from
	// endpoints other than starred-agents (e.g. recommended-agents) — agent records are
	// deduped through a shared, page-level store (usePaginatedAgentsMap) keyed by id, and
	// that field can be stale/inconsistent across endpoints. starred-agents category
	// membership (adjusted by our optimistic overrides) is the single source of truth.
	const favoriteAgentIds = useMemo(() => {
		const ids = new Set((starredAgents ?? []).map((agent) => agent.id));
		for (const [id, { favourite }] of Object.entries(favoriteOverrides)) {
			if (favourite) {
				ids.add(id);
			} else {
				ids.delete(id);
			}
		}
		return ids;
	}, [starredAgents, favoriteOverrides]);

	// Stabilize fetchAgentsByCategory and populateAgentCategories via refs
	// to avoid stale closure issues in useEffect — same pattern used by AgentMenu.
	const fetchAgentsByCategoryRef = useRef(fetchAgentsByCategory);
	const populateAgentCategoriesRef = useRef(populateAgentCategories);
	useEffect(() => {
		fetchAgentsByCategoryRef.current = fetchAgentsByCategory;
	}, [fetchAgentsByCategory]);
	useEffect(() => {
		populateAgentCategoriesRef.current = populateAgentCategories;
	}, [populateAgentCategories]);

	// Debounced server-side search (same pattern as rovo-ai-search's CarouselComponent).
	const debouncedSetSearchQuery = useDebouncedCallback((value: string) => {
		setSearchQuery(value);
	}, SEARCH_DEBOUNCE_MS);

	// Load agents when searchQuery changes (server-side search)
	useEffect(() => {
		if (!searchQuery) {
			void fetchAgentsByCategoryRef.current({ category: 'starred-agents', query: '' });
		}
		void fetchAgentsByCategoryRef.current({ category: 'all-agents', query: searchQuery });
		return () => {
			populateAgentCategoriesRef.current({
				category: 'starred-agents',
				agentsList: [],
				pageInfo: {
					startCursor: undefined,
					endCursor: undefined,
					hasNextPage: false,
					hasPreviousPage: false,
				},
			});
			populateAgentCategoriesRef.current({
				category: 'all-agents',
				agentsList: [],
				pageInfo: {
					startCursor: undefined,
					endCursor: undefined,
					hasNextPage: false,
					hasPreviousPage: false,
				},
			});
		};
	}, [searchQuery]);

	const favoriteAgents = useMemo(() => {
		// Cap only the server-confirmed portion of the list. Applying MAX_FAVORITE_AGENTS to
		// the combined (pending + base) list would silently evict an item on every toggle:
		// prepending pending items would push an existing starred agent out of view, while
		// appending them would push the just-favourited agent out of view — and since it's
		// already excluded from Recommended/Search via favoriteAgentIds, that would make it
		// disappear from the menu entirely. Letting the list temporarily exceed the cap by a
		// couple of items (until the next natural refetch settles it) is the better trade-off.
		const base: Agent[] = [];
		for (const agent of starredAgents ?? []) {
			if (favoriteAgentIds.has(agent.id)) {
				base.push(agent);
				if (base.length >= MAX_FAVORITE_AGENTS) {
					break;
				}
			}
		}
		const baseIds = new Set(base.map((agent) => agent.id));
		// Agents optimistically favourited (e.g. from Recommended/Search) that the server
		// hasn't returned in the starred-agents category yet — surface them immediately so
		// the agent shows up in Favorites without waiting on a refetch.
		const pendingAdditions: Agent[] = [];
		for (const { favourite, agent } of Object.values(favoriteOverrides)) {
			if (favourite && !baseIds.has(agent.id)) {
				pendingAdditions.push(agent);
			}
		}
		return [...pendingAdditions, ...base].map((agent) => ({ ...agent, favourite: true }));
	}, [starredAgents, favoriteOverrides, favoriteAgentIds]);

	const filteredRecommendedAgents = useMemo(() => {
		// Exclude agents already in favorites, and force `favourite: false` on the rest —
		// this list is defined as "not favourited", so we never trust the raw field here
		// (see favoriteAgentIds comment above for why it can't be trusted). Use the sticky
		// snapshot (see `stableRecommendedAgents` above) rather than `recommendedAgents`
		// directly, so a favourite/unfavourite round trip can't make an agent vanish.
		const result: Agent[] = [];
		for (const agent of stableRecommendedAgents) {
			if (!favoriteAgentIds.has(agent.id)) {
				result.push({ ...agent, favourite: false });
			}
		}
		return result;
	}, [stableRecommendedAgents, favoriteAgentIds]);

	// Search results come from server-side filtered all-agents category
	const filteredAgents = useMemo(() => {
		if (!searchQuery.trim()) {
			return null; // null = not in search mode
		}
		return (allAgentsList ?? []).map((agent) => ({
			...agent,
			favourite: favoriteAgentIds.has(agent.id),
		}));
	}, [searchQuery, allAgentsList, favoriteAgentIds]);

	const handleSelectAgent = useCallback(
		(agent: Agent) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'listItem',
					actionSubjectId: 'agent',
					source: 'confluenceObjectSidebarAgentsMenu',
					attributes: { agentId: agent.id },
				},
			}).fire();
			onSelectAgent(agent);
		},
		[onSelectAgent, createAnalyticsEvent],
	);

	const handleFavoriteToggle = useCallback(
		(agent: Agent) => {
			const isFavorited = !favoriteAgentIds.has(agent.id);

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'favoriteAgent',
					source: 'confluenceObjectSidebarAgentsMenu',
					attributes: { agentId: agent.id, isFavorited },
				},
			}).fire();

			// Optimistically flip the star and move the agent in/out of the Favorites
			// section immediately. Deliberately *not* triggering a fetchAgentsByCategory
			// refetch here — that flips the shared `isCategoryLoading` flag and collapses
			// the whole menu (Favorites + Recommended) down to a single spinner. The local
			// override above already keeps everything in sync for the current session.
			setFavoriteOverrides((prev) => ({
				...prev,
				[agent.id]: { favourite: isFavorited, agent },
			}));

			const callbacks = {
				onError: () => {
					setFavoriteOverrides((prev) => ({
						...prev,
						[agent.id]: { favourite: !isFavorited, agent },
					}));
				},
			};

			if (isFavorited) {
				void favouriteAgent.mutate(agent.id, callbacks);
			} else {
				void unfavouriteAgent.mutate(agent.id, callbacks);
			}
		},
		[favoriteAgentIds, createAnalyticsEvent, favouriteAgent, unfavouriteAgent],
	);

	const createAgentUrl = useMemo(
		() => getAtlassianStudioNLCreateUrl(cloudId, 'confluence-object-sidebar'),
		[cloudId],
	);

	const handleBrowseAgents = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'browseAgents',
				source: 'confluenceObjectSidebarAgentsMenu',
			},
		}).fire();
		onBrowseAgents();
	}, [onBrowseAgents, createAnalyticsEvent]);

	const handleSearchChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setSearchInputValue(e.target.value);
			debouncedSetSearchQuery(e.target.value);
		},
		[debouncedSetSearchQuery],
	);

	const handleCreateAgent = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'createAgent',
				source: 'confluenceObjectSidebarAgentsMenu',
			},
		}).fire();
		// Validate URL is a known Atlassian Studio domain before opening
		// to prevent potential open redirect or XSS vectors
		try {
			const url = new URL(createAgentUrl);
			const isAllowedOrigin = ALLOWED_STUDIO_ORIGINS.some(
				(allowed) => url.origin === new URL(allowed).origin,
			);
			if (isAllowedOrigin) {
				window.open(createAgentUrl, '_blank', 'noopener,noreferrer');
			}
		} catch {
			// Invalid URL — do nothing
		}
	}, [createAgentUrl, createAnalyticsEvent]);

	const isSearchMode = filteredAgents !== null;
	const isLoading = isStarredLoading || isAllAgentsLoading;

	const searchComponent = useMemo(
		() => (
			<AgentSearchInput
				key="search"
				ref={searchInputRef}
				value={searchInputValue}
				onChange={handleSearchChange}
				placeholder={formatMessage(messages.searchPlaceholder)}
			/>
		),
		[searchInputValue, handleSearchChange, formatMessage],
	);

	const footerItems = useMemo(
		() => [
			<MenuItem
				key="browse-agents"
				label={formatMessage(messages.browseAgents)}
				icon={
					<AiAgentIcon
						label={formatMessage(messages.browseAgents)}
						color={token('color.icon.subtle')}
					/>
				}
				onClick={handleBrowseAgents}
				testId="confluence-agent-menu-browse-agents"
			/>,
			<MenuItem
				key="create-agent"
				label={formatMessage(messages.createAgent)}
				icon={
					<AddIcon label={formatMessage(messages.createAgent)} color={token('color.icon.subtle')} />
				}
				onClick={handleCreateAgent}
				isExternalLink
				testId="confluence-agent-menu-create-agent"
			/>,
		],
		[formatMessage, handleBrowseAgents, handleCreateAgent],
	);

	const favoriteItems = useMemo(
		() =>
			favoriteAgents.map((agent) => (
				<AgentItem
					key={agent.id}
					agent={agent}
					isSelected={agent.id === selectedAgentId}
					onSelect={handleSelectAgent}
					onFavoriteToggle={handleFavoriteToggle}
					cloudId={cloudId}
					profileClient={profileClient}
				/>
			)),
		[
			favoriteAgents,
			selectedAgentId,
			handleSelectAgent,
			handleFavoriteToggle,
			cloudId,
			profileClient,
		],
	);

	const recommendedItems = useMemo(
		() =>
			filteredRecommendedAgents.map((agent) => (
				<AgentItem
					key={agent.id}
					agent={agent}
					isSelected={agent.id === selectedAgentId}
					onSelect={handleSelectAgent}
					onFavoriteToggle={handleFavoriteToggle}
					cloudId={cloudId}
					profileClient={profileClient}
				/>
			)),
		[
			filteredRecommendedAgents,
			selectedAgentId,
			handleSelectAgent,
			handleFavoriteToggle,
			cloudId,
			profileClient,
		],
	);

	const searchResultItems = useMemo(() => {
		if (!isSearchMode) return null;
		if (filteredAgents.length === 0) {
			return [
				<Box key="empty" xcss={styles.emptySection}>
					<Text color="color.text.subtlest">{formatMessage(messages.noAgentsFound)}</Text>
				</Box>,
			];
		}
		return filteredAgents.map((agent) => (
			<AgentItem
				key={agent.id}
				agent={agent}
				isSelected={agent.id === selectedAgentId}
				onSelect={handleSelectAgent}
				onFavoriteToggle={handleFavoriteToggle}
				cloudId={cloudId}
				profileClient={profileClient}
			/>
		));
	}, [
		isSearchMode,
		filteredAgents,
		selectedAgentId,
		handleSelectAgent,
		handleFavoriteToggle,
		formatMessage,
		cloudId,
		profileClient,
	]);

	const loadingComponent = useMemo(
		() => (
			<Box key="loading" xcss={styles.loadingWrapper}>
				<Spinner size="small" label={formatMessage(messages.loading)} />
			</Box>
		),
		[formatMessage],
	);

	const errorComponent = useMemo(
		() => (
			<Box key="error" xcss={styles.errorWrapper}>
				<Text color="color.text.subtlest">{formatMessage(messages.loadingError)}</Text>
			</Box>
		),
		[formatMessage],
	);

	const sections = useMemo(() => {
		const result = [
			{ items: [searchComponent] },
			...(agentsError
				? [{ items: [errorComponent] }]
				: isLoading
					? [{ items: [loadingComponent] }]
					: isSearchMode
						? [{ items: searchResultItems ?? [] }]
						: [
								...(favoriteItems.length > 0
									? [
											{
												title: formatMessage(messages.favoriteSection),
												items: favoriteItems,
											},
										]
									: []),
								...(recommendedItems.length > 0
									? [
											{
												title: formatMessage(messages.recommendedSection),
												items: recommendedItems,
											},
										]
									: []),
							]),
			{ items: footerItems },
		];
		return result.filter((section) => section.items.length > 0);
	}, [
		searchComponent,
		agentsError,
		errorComponent,
		isLoading,
		loadingComponent,
		isSearchMode,
		searchResultItems,
		favoriteItems,
		recommendedItems,
		footerItems,
		formatMessage,
	]);

	return (
		<>
			<Box xcss={styles.heading}>
				<Text as="span" size="small" weight="semibold" color="color.text">
					{formatMessage(messages.heading)}
				</Text>
			</Box>
			<ChatHeaderMenuList sections={sections} />
		</>
	);
};
